/**
 * Shared visual tokens for map year filter + analysis toolbox panels.
 * Aligns with LayerMapButton / CommunityMapButton (Additional Data popover).
 * @param {import('@mui/material').Theme} theme
 */
export function mapAnalysisPanelTokens(theme) {
  const isLight = theme.palette.mode === 'light'
  const gray = theme.palette.newColors?.gray || theme.palette.text.primary
  const lightGray = theme.palette.newColors?.lightGray || theme.palette.grey[200]
  const accent = theme.palette.primary.main
  const cyanGradient =
    theme.palette.newColors?.cyanGradient || theme.palette.primary.main
  const pinkGradient =
    theme.palette.newColors?.pinkGradient || theme.palette.error.main

  return {
    isLight,
    textPrimary: isLight ? gray : lightGray,
    textSecondary: theme.palette.text.secondary,
    surfaceBg: isLight ? theme.palette.background.paper : theme.palette.background.default,
    headerBg: isLight ? 'var(--color-background, #F8FAFC)' : 'transparent',
    rowBorder: theme.palette.divider,
    // Same subtle indigo wash as LayerMapButton / CommunityMapButton (not cyan).
    rowHoverBg: isLight ? 'rgba(99, 102, 241, 0.04)' : 'rgba(255, 255, 255, 0.04)',
    activeRowBg: isLight ? 'rgba(99, 102, 241, 0.08)' : 'rgba(99, 102, 241, 0.16)',
    gray,
    lightGray,
    accent,
    cyanGradient,
    pinkGradient,
  }
}

/**
 * Floating panel shell (year filter dropdown / analysis toolbox).
 * @param {import('@mui/material').Theme} theme
 */
export function mapAnalysisPanelShellSx(theme) {
  const t = mapAnalysisPanelTokens(theme)
  return {
    bgcolor: t.surfaceBg,
    borderRadius: 'var(--radius-2xl, 12px)',
    boxShadow: theme.shadows[8],
    overflow: 'hidden',
    boxSizing: 'border-box',
  }
}

/**
 * Panel header strip.
 * @param {import('@mui/material').Theme} theme
 */
export function mapAnalysisPanelHeaderSx(theme) {
  const t = mapAnalysisPanelTokens(theme)
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 1,
    px: 1.5,
    py: 1.25,
    borderBottom: `1px solid ${t.rowBorder}`,
    bgcolor: t.headerBg,
  }
}

/**
 * Caption / section title text.
 * @param {import('@mui/material').Theme} theme
 */
export function mapAnalysisTitleSx(theme) {
  const t = mapAnalysisPanelTokens(theme)
  return {
    fontWeight: 700,
    fontSize: 'var(--font-lg, 14px)',
    color: t.textPrimary,
    lineHeight: 1.35,
  }
}

/**
 * Compact icon actions in panel headers.
 * @param {import('@mui/material').Theme} theme
 * @param {{ active?: boolean }} [opts]
 */
export function mapAnalysisActionBtnSx(theme, opts = {}) {
  const t = mapAnalysisPanelTokens(theme)
  const active = Boolean(opts.active)
  return {
    minWidth: 32,
    width: 32,
    height: 32,
    p: 0,
    borderRadius: 'var(--radius-md, 6px)',
    color: active ? t.accent : t.textPrimary,
    bgcolor: active ? t.activeRowBg : 'transparent',
    border: `1px solid ${active ? t.accent : 'transparent'}`,
    '&:hover': {
      bgcolor: active ? t.activeRowBg : t.rowHoverBg,
      color: t.accent,
    },
    '&.Mui-disabled': {
      opacity: 0.35,
    },
  }
}

/**
 * Contained Show/Hide (or primary) action using brand gradients.
 * @param {import('@mui/material').Theme} theme
 * @param {boolean} isActiveVisible
 */
export function mapAnalysisShowHideButtonSx(theme, isActiveVisible) {
  const t = mapAnalysisPanelTokens(theme)
  return {
    color: '#fff',
    fontWeight: 700,
    borderRadius: 'var(--radius-lg, 8px)',
    textTransform: 'none',
    background: isActiveVisible ? t.pinkGradient : t.cyanGradient,
    '&:hover': {
      background: isActiveVisible ? t.pinkGradient : t.cyanGradient,
      opacity: 0.92,
    },
    '&.Mui-disabled': {
      color: 'rgba(255,255,255,0.7)',
      background: theme.palette.action.disabledBackground,
    },
  }
}
